import { SubscriptionManager, type SubscriptionSource } from "@mixitone/oom/SubscriptionManager";
import tauri from "lib/tauri";
import { listen } from "lib/tauri/MixitoneEvent";
import MixitoneController from "./MixitoneController";
import SessionController from "./SessionController";

export type SyncState = "idle" | "syncing" | "finished" | "error" | "offline";

interface State {
  syncState: SyncState;
  syncMessage: string;
  syncError?: string;

  // Card reader state
  cardReaders: Set<string>;
  cardReaderStates: Record<string, "empty" | "present">;
  cardReaderError?: string;
}

interface ChangeEvent {
  row: Record<string, any>;
  update: {
    row_id: string;
    source: "Server" | "Sync" | "Client";
    table_name: string;
    update_type: "Insert" | "Update" | "Delete";
    commit_timestamp?: string;
  };
}

interface CardEvent {
  reader: string;
}

const finishedTimeout = import.meta.env.VITE_TEST === "1" ? 0 : 5000;

class TauriController extends MixitoneController<State> {
  signedInAtSync?: boolean;

  debug(...args: any[]): void {
    console.log("[TauriController]", ...args);
  }

  override async initialize(_props: unknown) {
    this.signedInAtSync = this.get(SessionController).signedIn;
    this.state.syncState = "idle";
    await this.updateSyncState();

    // Initialize card reader state
    this.state.cardReaders = new Set();
    this.state.cardReaderStates = {};

    listen("mixitone:sync:progress", (event) => {
      this.debug("sync_progress", event);
      this.state.syncMessage = event.payload.message;
    });
    listen("mixitone:sync:started", (event) => {
      this.debug("started", event);
      this.actionUpdateSyncState("syncing", "Starting");
    });
    listen("mixitone:sync:finished", (event) => {
      this.debug("finished", event);
      this.actionUpdateSyncState("finished");
      setTimeout(() => {
        this.actionUpdateSyncState("idle");
      }, finishedTimeout);
    });
    listen("mixitone:sync:error", (event) => {
      this.debug("error", event);
      this.actionUpdateSyncState("idle");
      this.state.syncError = event.payload.message;
    });

    tauri.listen<ChangeEvent>("mixitonedb:change", (event) => {
      this.handleChange(event.payload);
    });

    // Card reader event listeners
    tauri.listen<CardEvent>("plugin-cardreader-added", (event) => {
      console.log("plugin-cardreader-added", event.payload);
      this.state.cardReaders.add(event.payload.reader);
      this.state.cardReaderStates[event.payload.reader] = "empty";
      this.state.cardReaderError = undefined;
      this.debug("cardreader-added", event.payload);
    });

    tauri.listen<CardEvent>("plugin-cardreader-removed", (event) => {
      this.state.cardReaders.delete(event.payload.reader);
      delete this.state.cardReaderStates[event.payload.reader];
      this.debug("cardreader-removed", event.payload);
    });

    tauri.listen<CardEvent>("plugin-cardreader-empty", (event) => {
      this.state.cardReaderStates[event.payload.reader] = "empty";
      this.debug("cardreader-empty", event.payload);
    });

    tauri.listen<CardEvent>("plugin-cardreader-present", (event) => {
      this.state.cardReaders.add(event.payload.reader);
      this.state.cardReaderStates[event.payload.reader] = "present";
      this.debug("cardreader-present", event.payload);
    });

    // Listen for card reader errors
    tauri.listen<{ message: string }>("plugin-cardreader-error", (event) => {
      this.state.cardReaderError = event.payload.message;
      this.debug("cardreader-error", event.payload);
    });

    setTimeout(() => {
      // @ts-expect-error
      tauri.invoke("plugin:cardreader|get_card_readers").then((readers: string[]) => {
        this.state.cardReaders = new Set(readers);
      });
    }, 1000);

    this.observe(() => {
      // @ts-expect-error
      window.tauriSyncState = this.state.syncState;
    });
  }

  actionUpdateSyncState(state: SyncState, message: string = "", error: string = "") {
    this.setState({
      syncState: state,
      syncMessage: message,
      syncError: error,
    });
  }

  get syncing() {
    return ["first_sync", "syncing"].includes(this.state.syncState);
  }

  get ready() {
    return ["idle", "syncing"].includes(this.state.syncState);
  }

  // Card reader getter methods
  get cardReadersAvailable() {
    return this.state.cardReaders.size > 0;
  }

  get cardPresent() {
    return Object.values(this.state.cardReaderStates).some((state) => state === "present");
  }

  handleChange(event: ChangeEvent) {
    let updateType: Parameters<typeof SubscriptionManager.prototype.emit>[1] = "UPDATE";
    let row = event.row;
    const update = event.update;

    switch (update.update_type) {
      case "Insert":
        updateType = "INSERT";
        break;
      case "Update":
        updateType = "UPDATE";
        break;
      case "Delete":
        updateType = "DELETE";
        break;
    }

    let source: SubscriptionSource = "server";

    switch (update.source) {
      case "Sync":
        source = "sync";
        break;
      case "Client":
        source = "client";
        break;
    }

    // If there's no row data then pass an object with the id
    if (!row || (Object.keys(row).length === 0 && update.row_id)) {
      row = { id: update.row_id };
    }

    const now = new Date();
    this.debug("handleChange", {
      date: now.toISOString(),
      table: update.table_name,
      id: update.row_id,
      updateType,
      source,
      event,
    });

    SubscriptionManager.instance.emit(
      update.table_name,
      updateType,
      row,
      updateType === "DELETE" ? row : undefined,
      update.commit_timestamp,
      source,
    );
  }

  async updateSyncState() {
    const result = await tauri.invoke<{
      running: boolean;
    }>("sync_state");

    if (result.running) {
      this.state.syncState = "syncing";
    } else {
      this.state.syncState = "idle";
    }
  }
}

export default TauriController;
